/**
 * Parked trades — the place "Remove" actually sends things.
 *
 * WHAT REMOVE USED TO DO, AND WHY IT WAS WRONG. It called DELETE. The row
 * vanished from the trade list and the lead reappeared, immediately, in the
 * "you have / they don't" list underneath — so the one control named for
 * getting rid of something put it straight back in front of you. The only way
 * to actually stop seeing a lead was "Hide", which is not a word anyone reaches
 * for when they mean "no, not this one".
 *
 * So Remove now PARKS: the trade keeps its row in the database with
 * status='hidden', which is what keeps the lead out of the surfaced lists (the
 * pane's `plannedKeys` counts parked rows too). This block is where those rows
 * live afterwards — collapsed by default, because a parking lot you have to
 * scroll past is just clutter, and every row can be put back in circulation or
 * thrown away for good.
 *
 * "For good" genuinely deletes, and that is the ONLY delete in the flow. It
 * returns the lead to the lists below — which is now the honest reading of it,
 * because you asked for the trade to stop existing rather than to go away.
 */
import { useState } from "react";
import { Archive, ChevronDown, RotateCcw, Trash2 } from "lucide-react";

import { speciesName } from "@/data/speciesNames";
import {
  VariantBadge,
  variantAlpha as vAlpha,
} from "@/components/dexVariantVisuals";
import { CompareSprite } from "@/components/trade/CompareSprite";
import type { PlannedTrade } from "@/components/trade/compareTypes";

export function ParkedTradesBlock({
  trades,
  friendName,
  onRestore,
  onDeleteForGood,
}: {
  /** The FULL trade list; this block picks out the parked ones itself. */
  trades: PlannedTrade[];
  friendName: string;
  onRestore: (t: PlannedTrade) => void;
  onDeleteForGood: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const parked = trades.filter((t) => t.status === "hidden");

  // Nothing parked, nothing to say. An empty parking lot needs no signage.
  if (parked.length === 0) return null;

  return (
    <section className="v2-neon-card-soft p-4 mt-4" data-testid="parked-trades-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="parked-trades-toggle"
        className="w-full flex items-center gap-2 text-left"
      >
        <Archive className="w-4 h-4 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />
        <span className="flex-1 min-w-0">
          <span className="block text-[12.5px] font-bold" style={{ color: "var(--text-primary)" }}>
            Parked
            <span
              className="ml-1.5 text-[10px] font-mono tabular-nums"
              style={{ color: "var(--text-tertiary)" }}
              data-testid="parked-trades-count"
            >
              {parked.length}
            </span>
          </span>
          <span className="block text-[11px] mt-0.5" style={{ color: "var(--text-tertiary)" }}>
            Out of circulation. They won't show in the lists below.
          </span>
        </span>
        <ChevronDown
          className="w-4 h-4 shrink-0 transition-transform"
          style={{ color: "var(--text-tertiary)", transform: open ? "rotate(180deg)" : undefined }}
          aria-hidden
        />
      </button>

      {open && (
        <div className="flex flex-col gap-2 mt-3" data-testid="parked-trades-list">
          {parked.map((t) => {
            const give = t.direction === "give";
            return (
              <div
                key={t.id}
                data-testid={`parked-trade-row-${t.id}`}
                className="rounded-xl px-3 py-2"
                style={{
                  background: "var(--surface-sunken)",
                  border: "1px dashed var(--border-subtle)",
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center opacity-70"
                    style={{ background: "var(--surface-dark-5)", border: `1px solid ${vAlpha(t.variant, 0.3)}` }}
                  >
                    <CompareSprite id={t.speciesId} shiny={t.variant === "shiny"} size={26} formId={t.formId} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold leading-tight truncate" style={{ color: "var(--text-secondary)" }}>
                      <span style={{ color: "var(--text-tertiary)", fontWeight: 700 }}>{give ? "GIVE" : "GET"}</span>{" "}
                      {speciesName(t.speciesId)}{" "}
                      <span style={{ color: "var(--text-ink-faint)", fontWeight: 600 }}>
                        {give ? "to" : "from"} {friendName}
                      </span>
                    </p>
                    <div className="mt-0.5">
                      <VariantBadge variant={t.variant} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2">
                  <button
                    type="button"
                    onClick={() => onRestore(t)}
                    data-testid={`parked-restore-${t.id}`}
                    className="flex-1 rounded-full text-[10.5px] font-semibold py-1.5 inline-flex items-center justify-center gap-1 min-h-[30px]"
                    style={{
                      background: "var(--accent-fill-glow-subtle)",
                      border: "1px solid var(--accent-fill-glow)",
                      color: "var(--accent-fill)",
                    }}
                  >
                    <RotateCcw className="w-3 h-3" /> Put back
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteForGood(t.id)}
                    data-testid={`parked-delete-${t.id}`}
                    className="flex-1 rounded-full text-[10.5px] font-semibold py-1.5 inline-flex items-center justify-center gap-1 min-h-[30px]"
                    style={{
                      background: "var(--surface-dark-9)",
                      border: "1px solid hsl(354 78% 56% / 0.35)",
                      color: "hsl(354 78% 56%)",
                    }}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
          <p className="text-[10.5px] leading-snug mt-0.5" style={{ color: "var(--text-ink-faint)" }}>
            Deleting drops the trade entirely. The Pokémon goes back to the lists below as a
            fresh lead.
          </p>
        </div>
      )}
    </section>
  );
}
